import {
  CalendarCheck,
  CalendarPlus,
  Check,
  ChevronDown,
  Dumbbell,
  Moon,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { DAY_LABELS } from "@/lib/days";
import type {
  PlannedWorkout,
  Workout,
  WorkoutLogConfig,
  WorkoutLogDayData,
  WorkoutLogWeekData,
} from "@/lib/schema";
import type { WidgetDayViewProps } from "@/widgets/types";
import { useAutoCollapse } from "@/widgets/useAutoCollapse";
import { PlanWeekModal } from "./PlanWeekModal";
import {
  canTick,
  isLoggingCompleted,
  isPlannedDay,
  isPlanningOn,
  keptPlanIds,
  offersRestDay,
  plannedOn,
  plannedWorkouts,
  planningAwardDay,
  todayIndexInWeek,
  unkeptPlansForDay,
  workoutFromPlan,
} from "./plan";
import { scoreDay } from "./scoring";

type Props = WidgetDayViewProps<WorkoutLogConfig>;

const PREVIEW_WORKOUTS: Workout[] = [
  { id: "preview-1", label: "Løpetur 5 km" },
  { id: "preview-2", label: "Styrke: overkropp" },
];

export function DayView({
  config,
  data,
  weekData,
  dayKey,
  onChangeDay,
  onChangeWeek,
  readOnly = false,
  week,
}: Props) {
  const realData = (data ?? { workouts: [] }) as WorkoutLogDayData;
  const showPreview = readOnly && realData.workouts.length === 0;
  const workouts: Workout[] = showPreview
    ? PREVIEW_WORKOUTS
    : realData.workouts;

  // The plan is invisible in the settings preview and on a week whose
  // snapshot has planning off (docs/adr/0026).
  const planning = !readOnly && isPlanningOn(config) && week !== undefined;
  const todayIndex = week ? todayIndexInWeek(week.startDate, new Date()) : 0;
  const plannedDay = planning && isPlannedDay(config, weekData, dayKey);
  const savedOn = planning ? plannedOn(config, weekData) : null;
  const plans = planning ? plannedWorkouts(config, weekData) : [];
  const plansById = new Map(plans.map((p) => [p.id, p]));
  const waiting = plannedDay
    ? unkeptPlansForDay(config, weekData, dayKey, data)
    : [];
  const restPlanned = plannedDay && !plans.some((p) => p.day === dayKey);
  const awardDay = planning ? planningAwardDay(config, weekData) : null;
  // The whole week's days, which the modal needs to know which plans are kept.
  const entry = week?.widgets.find((w) => w.config.id === config.id);
  const weekDays = entry && "days" in entry ? entry.days : {};

  // A hviledag declared before the plan covered this day is ignored on it.
  const restDay =
    realData.restDay === true &&
    (!planning || offersRestDay(config, weekData, dayKey));

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [addValue, setAddValue] = useState("");
  const [planOpen, setPlanOpen] = useState(false);

  const writeWorkouts = (next: Workout[]) => {
    // Invariant: restDay implies no økter, so logging clears the marker.
    // Deleting that økt again does not bring it back — you re-declare it.
    onChangeDay({
      workouts: next,
      restDay: next.length > 0 ? false : realData.restDay === true,
    } satisfies WorkoutLogDayData);
  };

  const toggleRestDay = () => {
    if (readOnly || realData.workouts.length > 0) return;
    onChangeDay({
      workouts: realData.workouts,
      restDay: !restDay,
    } satisfies WorkoutLogDayData);
  };

  // Ticking logs a workout that remembers the plan; unticking is deleting
  // that workout, which brings the plan back (docs/adr/0025).
  const tickPlan = (plan: PlannedWorkout) => {
    if (readOnly || !canTick(dayKey, todayIndex)) return;
    writeWorkouts([...realData.workouts, workoutFromPlan(plan)]);
  };

  const deleteWorkout = (id: string) => {
    if (readOnly) return;
    writeWorkouts(realData.workouts.filter((w) => w.id !== id));
  };

  const startEdit = (w: Workout) => {
    if (readOnly) return;
    setEditingId(w.id);
    setEditingValue(w.label);
  };

  const commitEdit = () => {
    if (editingId === null) return;
    const trimmed = editingValue.trim();
    if (trimmed.length > 0) {
      writeWorkouts(
        realData.workouts.map((w) =>
          w.id === editingId ? { ...w, label: trimmed } : w,
        ),
      );
    }
    setEditingId(null);
    setEditingValue("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const handleAdd = () => {
    if (readOnly) return;
    const trimmed = addValue.trim();
    if (trimmed.length === 0) return;
    const next: Workout = { id: crypto.randomUUID(), label: trimmed };
    writeWorkouts([...realData.workouts, next]);
    setAddValue("");
  };

  const savePlan = (next: WorkoutLogWeekData) => {
    onChangeWeek(next);
    setPlanOpen(false);
  };

  const score = scoreDay(
    config,
    { workouts } satisfies WorkoutLogDayData,
    dayKey,
    readOnly ? undefined : weekData,
  );

  // Ferdiglogget (CONTEXT.md): on a Planlagt dag, nothing planned left
  // unkept — so a planned rest day is green from the moment the plan is
  // saved. Elsewhere one økt, or a declared hviledag. Scoring is untouched
  // either way.
  const loggingCompleted =
    !readOnly &&
    (planning
      ? isLoggingCompleted(config, weekData, dayKey, data)
      : realData.workouts.length > 0 || restDay);

  const { open, toggle } = useAutoCollapse(dayKey, loggingCompleted);
  const collapsible = loggingCompleted;
  const collapsed = collapsible && !open;

  // Planning a week that is already over would cover no day at all.
  const offersPlanning = planning && (savedOn !== null || todayIndex <= 6);

  const summaryText =
    realData.workouts.length > 0
      ? realData.workouts.map((w) => w.label).join(" · ")
      : null;

  return (
    <section
      className={`widget-card workout-log${readOnly ? " read-only" : ""}${collapsed ? " collapsed" : ""}${loggingCompleted ? " logging-completed" : ""}`}
      data-testid={`widget-day-${config.id}`}
      data-logging-completed={loggingCompleted ? "true" : "false"}
    >
      <header className="widget-card-head">
        <h3 className="widget-card-title">{config.label}</h3>
        <div className="widget-card-head-right">
          {offersPlanning ? (
            <button
              type="button"
              className={`workout-plan-button${savedOn ? " planned" : ""}`}
              onClick={() => setPlanOpen(true)}
              data-testid={`workout-plan-button-${config.id}`}
              data-planned={savedOn ? "true" : "false"}
            >
              {savedOn ? (
                <>
                  <CalendarCheck size={14} aria-hidden="true" />
                  Uka er planlagt
                </>
              ) : (
                <>
                  <CalendarPlus size={14} aria-hidden="true" />
                  Planlegg uka
                </>
              )}
            </button>
          ) : null}
          <span
            className="widget-card-score"
            data-testid={`widget-score-${config.id}`}
          >
            <b>{score}</b>
            <span className="max">p</span>
          </span>
          {collapsible ? (
            <button
              type="button"
              className="widget-card-toggle"
              onClick={toggle}
              aria-label={collapsed ? "Utvid" : "Skjul"}
              aria-expanded={!collapsed}
              data-testid={`widget-collapse-${config.id}`}
            >
              <ChevronDown className="chev" size={16} />
            </button>
          ) : null}
        </div>
      </header>
      {collapsed ? (
        <div
          className="widget-card-summary"
          data-testid={`widget-summary-${config.id}`}
        >
          <span className="summary-line">
            {summaryText ? (
              <>
                <Dumbbell
                  className="summary-icon"
                  size={14}
                  aria-hidden="true"
                />
                {summaryText}
              </>
            ) : (
              <>
                <Moon className="summary-icon" size={14} aria-hidden="true" />
                {restPlanned ? "Hviledag (planlagt)" : "Hviledag"}
              </>
            )}
          </span>
        </div>
      ) : null}
      <div className="widget-card-body">
        <div className="widget-card-submeta">
          {workouts.length} {workouts.length === 1 ? "økt" : "økter"} ·{" "}
          {config.points}p per økt
          {awardDay && config.planning ? (
            <span data-testid={`workout-plan-award-${config.id}`}>
              {" "}
              · planlegging {config.planning.points}p (
              {DAY_LABELS[awardDay].toLowerCase()})
            </span>
          ) : null}
        </div>
        <div
          className="workout-items"
          data-testid={`workout-log-items-${config.id}`}
        >
          {waiting.map((plan) => {
            const tickable = canTick(dayKey, todayIndex);
            return (
              <div
                key={plan.id}
                className={`cl-item planned${tickable ? "" : " locked"}`}
                data-testid={`workout-planned-row-${plan.id}`}
              >
                {/* biome-ignore lint/a11y/useSemanticElements: custom-styled checkbox + label. */}
                <span
                  className="check"
                  role="checkbox"
                  aria-checked={false}
                  aria-disabled={!tickable}
                  tabIndex={tickable ? 0 : -1}
                  onClick={tickable ? () => tickPlan(plan) : undefined}
                  onKeyDown={
                    tickable
                      ? (e) => {
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            tickPlan(plan);
                          }
                        }
                      : undefined
                  }
                  data-testid={`workout-planned-checkbox-${plan.id}`}
                >
                  <Check size={14} />
                </span>
                <span className="cl-text">{plan.label}</span>
                {tickable ? null : (
                  <span className="cl-locked-day">planlagt</span>
                )}
              </div>
            );
          })}
          {workouts.length === 0 && waiting.length === 0 ? (
            restPlanned ? (
              <p
                className="hint"
                data-testid={`workout-log-planned-rest-${config.id}`}
              >
                Hviledag (planlagt)
              </p>
            ) : (
              <p
                className="hint"
                data-testid={`workout-log-empty-${config.id}`}
              >
                Ingen økter logget enda.
              </p>
            )
          ) : null}
          {workouts.map((w) => {
            const isEditing = editingId === w.id;
            // A workout ticked from a plan this card still shows carries the
            // ticked box; unticking it is deleting the workout.
            const fromPlan =
              w.plannedWorkoutId !== undefined &&
              plansById.has(w.plannedWorkoutId);
            return (
              <div
                key={w.id}
                className="cl-item"
                data-testid={`workout-row-${w.id}`}
              >
                {fromPlan ? (
                  // biome-ignore lint/a11y/useSemanticElements: custom-styled checkbox + label.
                  <span
                    className="check on"
                    role="checkbox"
                    aria-checked={true}
                    tabIndex={0}
                    onClick={() => deleteWorkout(w.id)}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        deleteWorkout(w.id);
                      }
                    }}
                    data-testid={`workout-planned-checkbox-${w.plannedWorkoutId}`}
                  >
                    <Check size={14} />
                  </span>
                ) : null}
                {isEditing ? (
                  <input
                    type="text"
                    className="workout-edit-input"
                    // biome-ignore lint/a11y/noAutofocus: focus required when entering edit mode.
                    autoFocus
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitEdit();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        cancelEdit();
                      }
                    }}
                    onBlur={commitEdit}
                    data-testid={`workout-edit-${w.id}`}
                  />
                ) : (
                  <button
                    type="button"
                    className="cl-text workout-label"
                    onClick={() => startEdit(w)}
                    disabled={readOnly}
                    data-testid={`workout-label-${w.id}`}
                  >
                    {w.label}
                  </button>
                )}
                {!readOnly ? (
                  <button
                    type="button"
                    className="cl-del icon-btn-delete"
                    onClick={() => deleteWorkout(w.id)}
                    aria-label="Slett"
                    data-testid={`workout-delete-${w.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
        {!readOnly ? (
          <div className="cl-add">
            <input
              type="text"
              placeholder="Logg en økt"
              value={addValue}
              onChange={(e) => setAddValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              data-testid={`workout-log-input-${config.id}`}
            />
            <button
              type="button"
              onClick={handleAdd}
              data-testid={`workout-log-add-button-${config.id}`}
            >
              + Logg økt
            </button>
          </div>
        ) : null}
        {/* Only reachable with no økter logged — the invariant forbids a
            hviledag alongside one, and logging clears the marker anyway. On
            a Planlagt dag rest is planned, never declared. */}
        {!readOnly && realData.workouts.length === 0 && !plannedDay ? (
          <div className="workout-log-actions">
            <button
              type="button"
              className={restDay ? "btn ghost" : "btn-primary"}
              onClick={toggleRestDay}
              data-testid={`rest-day-button-${config.id}`}
            >
              {restDay ? "Fjern hviledag" : "Hviledag"}
            </button>
          </div>
        ) : null}
      </div>
      {planOpen && week ? (
        <PlanWeekModal
          config={config}
          weekData={weekData}
          kept={keptPlanIds(weekDays)}
          todayIndex={todayIndex}
          year={week.year}
          weekNumber={week.weekNumber}
          onSave={savePlan}
          onClose={() => setPlanOpen(false)}
        />
      ) : null}
    </section>
  );
}
